// src/components/PinnedMessages.jsx
import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbtack,
  faTimes,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import "../App.css";

/**
 * Budowanie "odcisku palca" wiadomości, żeby uniknąć duplikatów.
 * Zakładam strukturę jak w LiveChat: { guestid, username, message, ... }
 */
const buildFingerprint = (msg) => {
  if (!msg) return "";
  const g = msg.guestid ?? "";
  const u = msg.username ?? "";
  const m = msg.message ?? "";
  return `${g}|${u}|${m}`;
};

/**
 * Wczytanie przypiętych wiadomości z localStorage
 * Klucz = pełna nazwa segmentu (segmentFullName).
 */
const loadPinned = (segmentFullName) => {
  if (!segmentFullName) return [];
  try {
    const raw = localStorage.getItem(segmentFullName);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
};

/**
 * Zapis przypiętych wiadomości do localStorage
 */
const savePinned = (segmentFullName, list) => {
  if (!segmentFullName) return;
  try {
    if (!list || list.length === 0) {
      localStorage.removeItem(segmentFullName);
    } else {
      localStorage.setItem(segmentFullName, JSON.stringify(list));
    }
  } catch {
    // jak localStorage padnie, to po prostu nic nie robimy
  }
};

/**
 * KLUCZ dla stanu zwinięcia
 */
const collapsedKey = (segmentFullName) =>
  `pinnedCollapsed:${segmentFullName}`;

/**
 * Wczytanie stanu collapsed dla segmentu
 * domyślnie: true (zwinięte)
 */
const loadCollapsed = (segmentFullName) => {
  if (!segmentFullName) return true;
  try {
    const raw = localStorage.getItem(collapsedKey(segmentFullName));
    if (raw === "0") return false;
    return true; // domyślnie zwinięte
  } catch {
    return true;
  }
};

/**
 * Zapis stanu collapsed
 */
const saveCollapsed = (segmentFullName, collapsed) => {
  if (!segmentFullName) return;
  try {
    localStorage.setItem(collapsedKey(segmentFullName), collapsed ? "1" : "0");
  } catch {
    // nic
  }
};

/**
 * Zewnętrzna funkcja pomocnicza do przypinania/odpinania wiadomości.
 *
 * - segmentFullName: pełna nazwa segmentu (string) -> klucz localStorage
 * - msg: obiekt wiadomości (guestid, username, message, itd.)
 */
export const togglePinMessageForSegment = (segmentFullName, msg) => {
  if (!segmentFullName || !msg) return;

  const current = loadPinned(segmentFullName);
  const fp = buildFingerprint(msg);

  let updated;
  const exists = current.some((m) => m._fp === fp);

  if (exists) {
    // odpinanie
    updated = current.filter((m) => m._fp !== fp);
  } else {
    // przypinanie
    const toStore = {
      ...msg,
      _fp: fp,
      pinnedAt: Date.now(),
    };
    updated = [...current, toStore];
  }

  savePinned(segmentFullName, updated);

  // powiadom komponent PinnedMessages, że coś się zmieniło
  window.dispatchEvent(
    new CustomEvent("pinned-messages:update", {
      detail: {
        segmentFullName,
        pinned: updated,
        lastAction: exists ? "unpin" : "pin",
      },
    })
  );
};

/**
 * Komponent wyświetlający przypięte wiadomości dla danego segmentu.
 *
 * Props:
 * - segmentFullName (string) – pełna nazwa segmentu (klucz w localStorage)
 * - className (opcjonalnie) – dodatkowa klasa dla kontenera
 */
export default function PinnedMessages({ segmentFullName, className = "" }) {
  const [pinned, setPinned] = useState([]);
  const [collapsed, setCollapsed] = useState(true);

  // tekst w nagłówku (komunikat)
  const [headerMessage, setHeaderMessage] = useState("Przypięte wiadomości");
  const [headerColor, setHeaderColor] = useState("#374151"); // domyślnie szary
  const headerTimerRef = useRef(null);

  const resetHeaderMessage = () => {
    setHeaderMessage("Przypięte wiadomości");
    setHeaderColor("#374151"); // wracamy do szarości
  };

  // wczytanie przypiętych + collapsed przy zmianie segmentu
  useEffect(() => {
    if (!segmentFullName) {
      setPinned([]);
      setCollapsed(true);
      resetHeaderMessage();
      return;
    }
    const initial = loadPinned(segmentFullName);
    setPinned(initial);

    const initialCollapsed = loadCollapsed(segmentFullName);
    setCollapsed(initialCollapsed);

    // przy zmianie segmentu resetujemy nagłówek
    resetHeaderMessage();

    if (headerTimerRef.current) {
      clearTimeout(headerTimerRef.current);
      headerTimerRef.current = null;
    }
  }, [segmentFullName]);

  // nasłuchiwanie na globalny event z togglePinMessageForSegment
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      if (detail.segmentFullName !== segmentFullName) return;

      setPinned(detail.pinned || []);

      if (detail.lastAction) {
        // czyścimy poprzedni timer
        if (headerTimerRef.current) {
          clearTimeout(headerTimerRef.current);
          headerTimerRef.current = null;
        }

        if (detail.lastAction === "pin") {
          setHeaderMessage("Wiadomość została przypięta");
          setHeaderColor("#16a34a"); // zielony
        } else if (detail.lastAction === "unpin") {
          setHeaderMessage("Wiadomość została odpięta");
          setHeaderColor("#dc2626"); // czerwony
        }

        // po chwili wracamy do standardowego tekstu
        headerTimerRef.current = setTimeout(() => {
          resetHeaderMessage();
          headerTimerRef.current = null;
        }, 2000);
      }
    };

    window.addEventListener("pinned-messages:update", handler);
    return () => {
      window.removeEventListener("pinned-messages:update", handler);
      if (headerTimerRef.current) {
        clearTimeout(headerTimerRef.current);
      }
    };
  }, [segmentFullName]);

  const handleUnpin = (msg) => {
    if (!segmentFullName || !msg) return;
    const fp = buildFingerprint(msg);
    const updated = (pinned || []).filter((m) => m._fp !== fp);
    setPinned(updated);
    savePinned(segmentFullName, updated);

    // lokalne odpięcie też emituje event z lastAction
    window.dispatchEvent(
      new CustomEvent("pinned-messages:update", {
        detail: {
          segmentFullName,
          pinned: updated,
          lastAction: "unpin",
        },
      })
    );
  };

  const handleToggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      saveCollapsed(segmentFullName, next);
      return next;
    });
  };

  if (!segmentFullName) return null;
  if (!pinned || pinned.length === 0) return null;

  // sortowanie: najnowsze przypięte na górze
  const sorted = [...pinned].sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0));

  return (
    <div
      className={className}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 3,
        background: "#f9fafb",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        padding: 8,
        marginBottom: 8,
      }}
    >
      {/* Nagłówek – kliknięcie zwija/rozwija panel */}
      <button
        type="button"
        onClick={handleToggleCollapsed}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: collapsed ? 0 : 6,
          fontSize: 12,
          fontWeight: 600,
          color: headerColor, // kolor zależny od akcji
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faThumbtack} />
          <span>
            {headerMessage}{" "}
            <span style={{ fontWeight: 400, color: "#6b7280" }}>
              ({pinned.length})
            </span>
          </span>
        </span>

        <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
      </button>

      {/* Lista przypiętych – pokazuj tylko, gdy nie jest zwinięta */}
      {!collapsed && (
        <div className="pinned_container">
          {sorted.map((msg, idx) => (
            <div
              key={msg._fp || idx}
              className="pinned_message_container"
            >
              <div style={{ flex: 1 }}>
                <strong>{msg.username}</strong>: {msg.message}
              </div>

              <button
                type="button"
                onClick={() => handleUnpin(msg)}
                title="Odepnij wiadomość"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 0,
                  color: "#6b7280",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
